// Setting a new password from a reset link.
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import { AuthCard, Banner, Field } from '../ui';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setFields({ confirm: 'Those two passwords do not match.' });
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await api.confirmPasswordReset({ token, password });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Something went wrong. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthCard title="Reset your password">
        <Banner kind="error">
          That link is missing its code. Copy the whole link from the email, or ask for a new one.
        </Banner>
        <p className="footnote">
          <Link to="/forgot-password">Send another link</Link>
        </p>
      </AuthCard>
    );
  }

  if (done) {
    return (
      <AuthCard title="Password changed">
        <Banner kind="ok">
          Your new password is set. Anyone signed in as you elsewhere has been signed out.
        </Banner>
        <Link to="/login">
          <button className="btn-primary full">Sign in</button>
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Choose a new password">
      {error && <Banner kind="error">{error}</Banner>}
      <form onSubmit={submit} noValidate>
        <Field
          label="New password"
          name="password"
          type="password"
          value={password}
          onChange={setPassword}
          error={fields.password}
          hint="At least 10 characters."
          autoComplete="new-password"
        />
        <Field
          label="Type it again"
          name="confirm"
          type="password"
          value={confirm}
          onChange={setConfirm}
          error={fields.confirm}
          autoComplete="new-password"
          enterKeyHint="go"
        />
        <button className="btn-primary full" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Set new password'}
        </button>
      </form>
    </AuthCard>
  );
}
